import {
  Modality,
  QCTaskDetailDicomRow,
  QCTaskDetailDXARow,
  QCTaskDetailExecRow,
  QCTaskDetailRow,
  QCTaskDetailSubheaderKindEnum,
  QCTaskDetailSubheaderRow,
  QCTaskRow,
  VisitInfo,
} from './types';
import { MODALITY_SUBHEADERS, SUBHEADER_KINDS } from './constants';
import { QCTaskLabel, SupportQCTask } from '@dynamika-shared/types/subjects';

export const isDetail = (row: QCTaskRow): row is QCTaskDetailRow =>
  row.type === 'detail';

export const isDetailSubheader = (
  row: QCTaskRow
): row is QCTaskDetailSubheaderRow => {
  if (row.type !== 'detail') return false;
  return (SUBHEADER_KINDS as readonly string[]).includes(
    row.detailKind as string
  );
};

export const isExecutionsSubheader = (
  row: QCTaskRow
): row is QCTaskDetailSubheaderRow =>
  row.type === 'detail' &&
  row.detailKind === QCTaskDetailSubheaderKindEnum.EXEC;

export const isModalitySubheader = (
  row: QCTaskRow
): row is QCTaskDetailSubheaderRow => {
  if (row.type !== 'detail') return false;
  return MODALITY_SUBHEADERS.includes(
    row.detailKind as (typeof MODALITY_SUBHEADERS)[number]
  );
};

export const isExecDetail = (row: QCTaskRow): row is QCTaskDetailExecRow =>
  row.type === 'detail' && row.detailKind === 'exec';

export const isDXADetail = (row: QCTaskRow): row is QCTaskDetailDXARow =>
  row.type === 'detail' && row.detailKind === 'dxa';

export const isDicomDetail = (row: QCTaskRow): row is QCTaskDetailDicomRow =>
  row.type === 'detail' &&
  ['ct', 'xray', 'mri', 'pdf', 'csv', 'misc'].includes(row.detailKind);

export const toFamily = (mod?: string): Modality => {
  const upper = (mod ?? '').toUpperCase();
  if (upper.startsWith('DXA')) return Modality.DXA;
  if (upper === 'CT') return Modality.CT;
  if (upper === 'XRAY' || upper === 'X-RAY' || upper === 'XR') {
    return Modality.XRAY;
  }
  if (upper.startsWith('MRI')) return Modality.MRI;
  if (upper === 'PDF') return Modality.PDF;
  if (upper === 'CSV') return Modality.CSV;
  return Modality.MISC;
};

export const makeQcId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `qc-task-${Date.now()}`;
};

export const lastColLabelForSubheader = (
  kind: QCTaskDetailSubheaderKindEnum
): string => {
  switch (kind) {
    case QCTaskDetailSubheaderKindEnum.EXEC:
      return 'Status';
    case QCTaskDetailSubheaderKindEnum.DXA:
      return 'Workstation';
    case QCTaskDetailSubheaderKindEnum.CT:
    case QCTaskDetailSubheaderKindEnum.XRAY:
    case QCTaskDetailSubheaderKindEnum.MRI:
    case QCTaskDetailSubheaderKindEnum.PDF:
    case QCTaskDetailSubheaderKindEnum.CSV:
    case QCTaskDetailSubheaderKindEnum.MISC:
      return 'Dicom Series UID';
    default:
      return '';
  }
};

export const isExecSubheaderRow = (
  row: QCTaskRow
): row is QCTaskDetailSubheaderRow =>
  row.type === 'detail' &&
  row.detailKind === QCTaskDetailSubheaderKindEnum.EXEC;

type LabelBucket = {
  family: Modality;
  items: QCTaskLabel[];
};

type ByModalityMap = Record<string, LabelBucket>;

type ByModalityIndexMap = Record<string, Map<string, number>>;

const normalizeText = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const hasRenderableLabel = (label: QCTaskLabel): boolean =>
  Boolean(
    normalizeText(label.labelName) ||
      normalizeText(label.bodyRegion) ||
      normalizeText(label.dicomSeriesUid) ||
      normalizeText(label.seriesInstanceUid) ||
      normalizeText(label.acquisitionDate)
  );

const getLabelIdentityKey = (label: QCTaskLabel): string => {
  const modalityKey = normalizeText(label.modality) ?? Modality.MISC;
  const labelKey = normalizeText(label.labelName);

  if (labelKey) {
    return [modalityKey, 'label', labelKey].join('|');
  }

  const seriesKey =
    normalizeText(label.dicomSeriesUid) ?? normalizeText(label.seriesInstanceUid);
  if (seriesKey) {
    return [modalityKey, 'series', seriesKey].join('|');
  }

  const bodyRegionKey = normalizeText(label.bodyRegion);
  if (bodyRegionKey) {
    return [modalityKey, 'body-region', bodyRegionKey].join('|');
  }

  const acquisitionDateKey = normalizeText(label.acquisitionDate);
  if (acquisitionDateKey) {
    return [modalityKey, 'date', acquisitionDateKey].join('|');
  }

  return [modalityKey, 'empty'].join('|');
};

const mergeLabelRecord = (
  existing: QCTaskLabel,
  incoming: QCTaskLabel
): QCTaskLabel => ({
  modality: normalizeText(existing.modality) ?? normalizeText(incoming.modality),
  labelName: normalizeText(existing.labelName) ?? normalizeText(incoming.labelName),
  bodyRegion: normalizeText(existing.bodyRegion) ?? normalizeText(incoming.bodyRegion),
  acquisitionDate:
    normalizeText(existing.acquisitionDate) ??
    normalizeText(incoming.acquisitionDate),
  dicomSeriesUid:
    normalizeText(existing.dicomSeriesUid) ??
    normalizeText(incoming.dicomSeriesUid),
  seriesInstanceUid:
    normalizeText(existing.seriesInstanceUid) ??
    normalizeText(incoming.seriesInstanceUid),
});

export const HEADER_DETAIL_KIND_BY_MODALITY: Record<
  Modality,
  QCTaskDetailSubheaderKindEnum
> = {
  [Modality.DXA]: QCTaskDetailSubheaderKindEnum.DXA,
  [Modality.CT]: QCTaskDetailSubheaderKindEnum.CT,
  [Modality.MRI]: QCTaskDetailSubheaderKindEnum.MRI,
  [Modality.XRAY]: QCTaskDetailSubheaderKindEnum.XRAY,
  [Modality.PDF]: QCTaskDetailSubheaderKindEnum.PDF,
  [Modality.CSV]: QCTaskDetailSubheaderKindEnum.CSV,
  [Modality.MISC]: QCTaskDetailSubheaderKindEnum.MISC,
};

export const getDetailKindForHeader = (
  family: Modality
): QCTaskDetailSubheaderKindEnum => {
  return (
    HEADER_DETAIL_KIND_BY_MODALITY[family] ?? QCTaskDetailSubheaderKindEnum.MISC
  );
};

const collectAllLabels = (task: SupportQCTask): QCTaskLabel[] => {
  const labels: QCTaskLabel[] = task.labels?.labels ?? [];
  const dxaLabels: QCTaskLabel[] = task.labels?.dxa ?? [];
  const dicomLabels: QCTaskLabel[] = task.labels?.dicom ?? [];
  return [...labels, ...dxaLabels, ...dicomLabels];
};

const groupLabelsByModality = (allLabels: QCTaskLabel[]): ByModalityMap => {
  const byModality: ByModalityMap = {};
  const byModalityIndex: ByModalityIndexMap = {};

  for (const lbl of allLabels) {
    if (!hasRenderableLabel(lbl)) {
      continue;
    }

    const modalityRaw = lbl.modality;
    const family = toFamily(modalityRaw);
    const modKey = normalizeText(modalityRaw) ?? Modality.MISC;
    const identityKey = getLabelIdentityKey(lbl);

    const bucket = byModality[modKey];
    if (bucket) {
      const identityIndex = byModalityIndex[modKey] ?? new Map<string, number>();
      byModalityIndex[modKey] = identityIndex;

      const existingIndex = identityIndex.get(identityKey);
      if (existingIndex === undefined) {
        identityIndex.set(identityKey, bucket.items.length);
        bucket.items.push(lbl);
      } else {
        bucket.items[existingIndex] = mergeLabelRecord(
          bucket.items[existingIndex],
          lbl
        );
      }
    } else {
      byModality[modKey] = { family, items: [lbl] };
      byModalityIndex[modKey] = new Map<string, number>([[identityKey, 0]]);
    }
  }

  return byModality;
};

const createCommonLabelRow = (
  taskId: string,
  family: Modality,
  index: number,
  modKey: string,
  lbl: QCTaskLabel
) => {
  const col1Value =
    lbl.modality ?? modKey ?? lbl.labelName ?? lbl.bodyRegion ?? '—';

  const col2Value =
    lbl.labelName ?? lbl.bodyRegion ?? lbl.modality ?? modKey ?? '—';

  return {
    type: 'detail' as const,
    key: `${taskId}-${family.toLowerCase()}-${index}`,
    groupKey: taskId,
    label: col1Value,
    detailLabel: col2Value,
    acquisitionDate: lbl.acquisitionDate ?? '',
  };
};

const createModalityDetailRow = (
  family: Modality,
  common: ReturnType<typeof createCommonLabelRow>,
  lbl: QCTaskLabel
): QCTaskRow => {
  const dicomUid = lbl.dicomSeriesUid ?? lbl.seriesInstanceUid ?? '';

  switch (family) {
    case Modality.DXA:
      return {
        ...common,
        detailKind: 'dxa',
        workstation: lbl.bodyRegion ?? '—',
      } as QCTaskRow;
    case Modality.CT:
      return {
        ...common,
        detailKind: 'ct',
        dicomSeriesUid: dicomUid,
      } as QCTaskRow;
    case Modality.XRAY:
      return {
        ...common,
        detailKind: 'xray',
        dicomSeriesUid: dicomUid,
      } as QCTaskRow;
    case Modality.PDF:
      return {
        ...common,
        detailKind: 'pdf',
        dicomSeriesUid: dicomUid,
      } as QCTaskRow;
    case Modality.CSV:
      return {
        ...common,
        detailKind: 'csv',
        dicomSeriesUid: dicomUid,
      } as QCTaskRow;
    default:
      return {
        ...common,
        detailKind: 'misc',
        dicomSeriesUid: dicomUid,
      } as QCTaskRow;
  }
};

const buildHeaderRowForModality = (
  taskId: string,
  family: Modality,
  modKey: string
): QCTaskRow =>
  ({
    type: 'detail',
    key: `${taskId}-${family.toLowerCase()}-h-${modKey}`,
    groupKey: taskId,
    detailKind: getDetailKindForHeader(family),
    modalityLabel: modKey,
  }) as QCTaskRow;

export const buildExecutionRows = (
  taskId: string,
  task: SupportQCTask
): QCTaskRow[] => {
  const executions = task.executions ?? [];
  const execRows: QCTaskRow[] = [];

  execRows.push({
    type: 'detail',
    key: `${taskId}-exec-h`,
    groupKey: taskId,
    detailKind: QCTaskDetailSubheaderKindEnum.EXEC,
  });

  let execIndex = 0;
  for (const ex of executions) {
    const wf = ex?.workflowExecution;
    const isWf = ex?.isTaskWorkflowExecution;

    execRows.push({
      type: 'detail',
      key: `${taskId}-exec-${execIndex}`,
      groupKey: taskId,
      detailKind: 'exec',
      label:
        wf?.workflowName ||
        wf?.workflowType ||
        wf?.module ||
        `Execution ${execIndex + 1}`,
      startDate: wf?.started ?? '',
      endDate: wf?.finished ?? '',
      assignee: task.taskAssigneeUser?.fullName ?? '',
      assigneeEmail: task.taskAssigneeUser?.email ?? '',
      status: wf?.status ?? isWf?.status ?? '',
    });

    execIndex += 1;
  }

  return execRows;
};

export const buildModalityRows = (
  taskId: string,
  task: SupportQCTask
): QCTaskRow[] => {
  const allLabels = collectAllLabels(task);
  const byModality = groupLabelsByModality(allLabels);

  const modalityRows: QCTaskRow[] = [];

  for (const [modKey, bucket] of Object.entries(byModality)) {
    const { family, items } = bucket;

    modalityRows.push(buildHeaderRowForModality(taskId, family, modKey));

    items.forEach((lbl, index) => {
      const common = createCommonLabelRow(taskId, family, index, modKey, lbl);
      const detailRow = createModalityDetailRow(family, common, lbl);
      modalityRows.push(detailRow);
    });
  }

  return modalityRows;
};

export const buildGroupRow = (
  task: SupportQCTask,
  visitName: string
): QCTaskRow => {
  const taskId = task.taskId ?? task.id ?? makeQcId();

  return {
    type: 'group',
    key: taskId,
    task: `QC – ${visitName}`,
    started: task.startDate ?? task.taskCreated ?? '',
    finished: task.completionDate ?? '',
    assignee: task.taskAssigneeUser?.fullName ?? '',
    assigneeEmail: task.taskAssigneeUser?.email ?? '',
    status: task.isTaskStatus ?? '',
  };
};

export const getVisitName = (visit: VisitInfo): string => {
  if (visit.visitName) {
    return visit.visitName;
  }

  if (typeof visit.visitOrder === 'number') {
    return `Visit ${visit.visitOrder}`;
  }

  return 'Visit';
};
